import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import {
  submitIdCardReprintRequest,
  listIdCardReprintApprovals,
  approveIdCardReprintRequest,
  rejectIdCardReprintRequest,
  getIdCardReprintShroffVoucherPdf,
  confirmIdCardReprintManualPayment,
} from "../../services/idCardReprintService";
import { listMembers } from "../../services/memberService";
import {
  TOKENS,
  Icon,
  ICONS,
  Avatar,
  Pill,
  EmptyState,
  Spinner,
  inputClass,
  labelClass,
  btnPrimary,
  btnSecondary,
  Pagination,
  usePagination,
} from "../../components/common/DashboardUI";
import ReceiptFileInput from "../../components/common/ReceiptFileInput";

// ID card REPRINT workflow (lost card -> director approval -> pay -> reprint
// -> issue) — see IdCardReprintController. This is a separate resource from
// the ordinary print/issue queue in idCardService.js/IdCardPrinting.jsx,
// which this page never touches: once a reprint's payment is confirmed
// (Confirm Payment tab below, or the shared Manual Approvals queue on the
// Payments page), the backend flips card_status to "Pending Print" itself
// and the member shows up in the existing Print Queue exactly like a normal
// first-time card — nothing here talks to /id-card-print-requests.
//
// Three tabs, each independently permission-gated (module visibility itself
// is gated in adminNav.js by "view_id_card_print_requests", shared with the
// Print ID Cards page, so any role that touches ID cards can reach this
// page — each tab then hides itself if the signed-in role lacks the
// finer-grained permission it needs):
//   - Submit Request      -> "submit_id_card_reprint_requests" (Instructor)
//   - Reprint Approvals   -> "view_id_card_reprint_approvals" / approve /
//                            reject (Director/Admin only)
//   - Confirm Payment     -> "submit_manual_payment_receipts" + "view_payments"
//     (Instructor) — there is no online/CyberExt path for reprints on the
//     backend today (IdCardReprintController::approve never builds a
//     payment_payload), so shroff/counter payment is the only way a reprint
//     fee actually gets marked paid.

function errInfo(err, fallback) {
  const data = err?.response?.data;
  return { message: data?.message || fallback, fields: data?.errors || {} };
}

const fullName = (m) => [m?.title, m?.initials || m?.name_denoted_by_initials, m?.lastname].filter(Boolean).join(" ") || "—";
const money = (v) => (v == null || v === "" ? "—" : `LKR ${Number(v).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`);
const fmtDateTime = (v) => {
  if (!v) return "—";
  const s = String(v).replace("T", " ");
  return s.length >= 16 ? s.slice(0, 16) : s;
};

// ── Modal shell (matches the pattern used across the admin panel) ───────────
function Modal({ title, onClose, children, size = "md" }) {
  const width = size === "lg" ? "max-w-2xl" : "max-w-md";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={`relative w-full ${width} bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <Icon path={ICONS.x} className="w-5 h-5" width={2} />
        </button>
        <h2 className="text-lg font-bold mb-4 pr-8" style={{ color: TOKENS.NAVY }}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

// Opens a blob in a pre-opened tab (must be window.open'd synchronously
// inside the triggering click to avoid the popup blocker).
function openBlobInTab(blob, tab) {
  const url = URL.createObjectURL(blob);
  if (tab && !tab.closed) tab.location.href = url;
  else window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// ── Member picker (debounced search over GET /api/members) ─────────────────
// Used only by the Submit Request tab — a system user (Instructor/operator)
// has no member_id of their own, so they look one up by name/email before
// submitting a reprint on that member's behalf.
function MemberPicker({ member, onChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!query.trim() || member) {
      setResults([]);
      return undefined;
    }
    setSearching(true);
    const t = setTimeout(() => {
      listMembers({ search: query.trim() })
        .then((r) => setResults(r.slice(0, 8)))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query, member]);

  if (member) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2.5">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar name={fullName(member)} size={32} />
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
              {fullName(member)}
            </p>
            <p className="text-xs text-gray-400 truncate">{member.email || member.member_code}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            onChange(null);
            setQuery("");
          }}
          className="text-xs font-semibold text-gray-400 hover:text-gray-700 shrink-0"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
          <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
        </span>
        <input
          className={`${inputClass} pl-9`}
          placeholder="Search member by name, email or reg. no…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
        />
      </div>
      {open && query.trim() && (
        <div className="absolute z-10 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-100 max-h-64 overflow-y-auto">
          {searching ? (
            <div className="px-4 py-3 text-sm text-gray-400">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400">No members found.</div>
          ) : (
            results.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  onChange(m);
                  setOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left"
              >
                <Avatar name={fullName(m)} size={28} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                    {fullName(m)}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{m.email}</p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Submit Request tab ───────────────────────────────────────────────────
function SubmitRequestTab() {
  const [member, setMember] = useState(null);
  const [reason, setReason] = useState("");
  const [policeReport, setPoliceReport] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [result, setResult] = useState(null);

  const fieldError = (key) => {
    const msgs = fieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  const reset = () => {
    setMember(null);
    setReason("");
    setPoliceReport(null);
    setError("");
    setFieldErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !member || !policeReport || !reason.trim()) return;
    setSubmitting(true);
    setError("");
    setFieldErrors({});
    try {
      const r = await submitIdCardReprintRequest({
        member_id: member.id,
        reason: reason.trim(),
        police_report: policeReport,
      });
      toast.success(r.message || "Reprint request submitted.");
      setResult({ ...r.reprint_request, message: r.message, memberName: fullName(member) });
      reset();
    } catch (err) {
      const info = errInfo(err, "Could not submit the reprint request.");
      setError(info.message);
      setFieldErrors(info.fields);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl space-y-4">
      <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
        <div>
          <h3 className="font-bold text-base mb-1" style={{ color: TOKENS.NAVY }}>
            Submit a reprint request
          </h3>
          <p className="text-sm text-gray-500">
            For a walk-in member who lost their card. This bumps their card version immediately — the old QR stops
            working right away — and queues the request for a Director/Admin to approve.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>Member</label>
            <MemberPicker member={member} onChange={setMember} />
            {fieldError("member_id") && <p className="text-xs text-red-600 mt-1">{fieldError("member_id")}</p>}
          </div>

          <div>
            <label className={labelClass}>Reason</label>
            <textarea
              className={`${inputClass} resize-none`}
              rows={3}
              maxLength={1000}
              placeholder="e.g. Lost ID card"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />
            {fieldError("reason") && <p className="text-xs text-red-600 mt-1">{fieldError("reason")}</p>}
          </div>

          <div>
            <label className={labelClass}>
              Police report <span className="text-gray-400 font-normal">(PDF or image, max 5MB)</span>
            </label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              className={inputClass}
              onChange={(e) => setPoliceReport(e.target.files?.[0] ?? null)}
              required
            />
            {fieldError("police_report") && <p className="text-xs text-red-600 mt-1">{fieldError("police_report")}</p>}
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button type="submit" disabled={submitting || !member || !policeReport || !reason.trim()} className={`${btnPrimary} w-full`}>
            {submitting ? "Submitting…" : "Submit Reprint Request"}
          </button>
        </form>
      </div>

      {result && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-800 space-y-1">
          <p>
            {result.message} Card version for <strong>{result.memberName}</strong> is now{" "}
            <span className="font-mono font-semibold">v{result.new_card_version}</span>.
          </p>
          {result.can_pay ? (
            <p className="text-emerald-700">
              Payable now — reference <span className="font-mono font-semibold">{result.external_payment_reference}</span>.
            </p>
          ) : (
            <p className="text-emerald-700">A Director/Admin needs to approve this on the Reprint Approvals tab before it can be paid.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Reprint Approvals tab (Director/Admin) ──────────────────────────────
function ApprovalsTab({ canApprove, canReject, canViewVoucher }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // full row object for the open detail modal
  const [acting, setActing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionNote, setRejectionNote] = useState("");
  const [approvedInfo, setApprovedInfo] = useState(null); // payment_reference etc. shown right after approving
  const [downloadingVoucher, setDownloadingVoucher] = useState(false);

  const fetchRequests = useCallback(() => {
    setLoading(true);
    listIdCardReprintApprovals()
      .then(setRequests)
      .catch((err) => toast.error(errInfo(err, "Could not load reprint approvals.").message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const closeDetail = () => {
    setSelected(null);
    setRejecting(false);
    setRejectionNote("");
    setApprovedInfo(null);
  };

  const handleApprove = async () => {
    if (!selected || acting) return;
    setActing(true);
    try {
      const r = await approveIdCardReprintRequest(selected.id);
      toast.success(r.message || "Reprint request approved.");
      setApprovedInfo(r.reprint_request);
      fetchRequests();
    } catch (err) {
      toast.error(errInfo(err, "Could not approve this reprint request.").message);
    } finally {
      setActing(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    if (!selected || acting || !rejectionNote.trim()) return;
    setActing(true);
    try {
      const r = await rejectIdCardReprintRequest(selected.id, rejectionNote.trim());
      toast.success(r.message || "Reprint request rejected.");
      closeDetail();
      fetchRequests();
    } catch (err) {
      toast.error(errInfo(err, "Could not reject this reprint request.").message);
    } finally {
      setActing(false);
    }
  };

  const handleDownloadVoucher = async () => {
    if (!selected || downloadingVoucher) return;
    const tab = window.open("", "_blank");
    setDownloadingVoucher(true);
    try {
      const blob = await getIdCardReprintShroffVoucherPdf(selected.id);
      openBlobInTab(blob, tab);
    } catch (err) {
      tab?.close?.();
      toast.error(errInfo(err, "Could not load the shroff voucher.").message);
    } finally {
      setDownloadingVoucher(false);
    }
  };

  const { page, setPage, totalPages, pageItems: pagedRequests } = usePagination(requests);

  return (
    <div>
      <div className="bg-white rounded-2xl shadow-sm p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : requests.length === 0 ? (
          <EmptyState icon={ICONS.shield} text="No reprint requests are pending approval." />
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-left min-w-[760px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Member</th>
                  <th className="py-2 px-3 font-medium">Reason</th>
                  <th className="py-2 px-3 font-medium">Submitted</th>
                  <th className="py-2 px-3 font-medium">Status</th>
                  <th className="py-2 pl-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {pagedRequests.map((r) => (
                  <tr key={r.id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={fullName(r)} size={34} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                            {fullName(r)}
                          </p>
                          <p className="text-xs text-gray-400 truncate">{r.member_code}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 max-w-[240px] truncate">{r.reason}</td>
                    <td className="py-3 px-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                    <td className="py-3 px-3">
                      <Pill>{r.approval_status}</Pill>
                    </td>
                    <td className="py-3 pl-3 text-right">
                      <button
                        onClick={() => setSelected(r)}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-50"
                      >
                        <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="pt-4 mt-2 border-t border-gray-100">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {selected && (
        <Modal title="Reprint Request" onClose={closeDetail} size="lg">
          <div className="space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar name={fullName(selected)} size={40} />
                <div className="min-w-0">
                  <p className="text-base font-bold truncate" style={{ color: TOKENS.NAVY }}>
                    {fullName(selected)}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{selected.member_code}</p>
                </div>
              </div>
              <Pill>{approvedInfo ? "Approved" : selected.approval_status}</Pill>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {[
                ["Reason", selected.reason],
                ["Submitted", fmtDateTime(selected.created_at)],
                ["Payable amount", money(selected.payable_amount)],
              ]
                .filter(([, v]) => v !== null && v !== undefined && v !== "")
                .map(([label, value]) => (
                  <div key={label} className="min-w-0">
                    <dt className="text-xs text-gray-400">{label}</dt>
                    <dd className="text-sm font-medium text-gray-700 break-words" style={{ color: TOKENS.NAVY }}>
                      {value}
                    </dd>
                  </div>
                ))}
            </dl>

            {selected.police_report_file_url && (
              <a
                href={selected.police_report_file_url}
                target="_blank"
                rel="noreferrer"
                className={`${btnSecondary} !w-auto px-4 inline-flex items-center gap-2`}
              >
                <Icon path={ICONS.download} className="w-4 h-4" width={1.8} />
                View Police Report
              </a>
            )}

            {!approvedInfo && selected.approval_status === "Pending" && (canApprove || canReject) && (
              <div className="pt-4 border-t border-gray-100 space-y-3">
                {!rejecting ? (
                  <div className="flex flex-wrap gap-3">
                    {canApprove && (
                      <button type="button" onClick={handleApprove} disabled={acting} className={`${btnPrimary} !w-auto px-6`}>
                        {acting ? "Approving…" : "Approve"}
                      </button>
                    )}
                    {canReject && (
                      <button
                        type="button"
                        onClick={() => setRejecting(true)}
                        disabled={acting}
                        className="inline-flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 font-semibold rounded-lg text-sm px-6 py-2.5 transition"
                      >
                        Reject
                      </button>
                    )}
                  </div>
                ) : (
                  <form onSubmit={handleReject} className="space-y-3">
                    <div>
                      <label className={labelClass}>Rejection note</label>
                      <textarea
                        className={`${inputClass} resize-none`}
                        rows={2}
                        value={rejectionNote}
                        onChange={(e) => setRejectionNote(e.target.value)}
                        required
                        autoFocus
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        disabled={acting || !rejectionNote.trim()}
                        className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg text-sm px-6 py-2.5 transition"
                      >
                        {acting ? "Rejecting…" : "Confirm Reject"}
                      </button>
                      <button type="button" onClick={() => setRejecting(false)} className={`${btnSecondary} !w-auto px-6`}>
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}

            {approvedInfo && (
              <div className="pt-4 border-t border-gray-100 space-y-3">
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-800 space-y-1">
                  <p>Approved. Payable amount: {money(approvedInfo.payable_amount)}.</p>
                  <p>
                    Payment reference:{" "}
                    <span className="font-mono font-semibold">{approvedInfo.external_payment_reference}</span> — the member
                    can now pay online or at the counter from their own dashboard, or give this reference to staff for
                    counter payment via the Confirm Payment tab once paid.
                  </p>
                </div>
                {canViewVoucher && (
                  <button
                    type="button"
                    onClick={handleDownloadVoucher}
                    disabled={downloadingVoucher}
                    className={`${btnSecondary} !w-auto px-4 inline-flex items-center gap-2`}
                  >
                    <Icon path={ICONS.download} className="w-4 h-4" width={1.8} />
                    {downloadingVoucher ? "Opening…" : "Download Shroff Voucher"}
                  </button>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Confirm Payment tab (shroff/manual — the only payment path a reprint fee
// can actually be marked Paid through today) ────────────────────────────
function ConfirmPaymentTab({ canConfirm, canViewVoucher }) {
  const [voucherReprintId, setVoucherReprintId] = useState("");
  const [downloadingVoucher, setDownloadingVoucher] = useState(false);

  const [paymentReference, setPaymentReference] = useState("");
  const [paidAmount, setPaidAmount] = useState("");
  const [paidDate, setPaidDate] = useState(new Date().toISOString().slice(0, 10));
  const [receiptReference, setReceiptReference] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [result, setResult] = useState(null);

  const fieldError = (key) => {
    const msgs = fieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  const handleDownloadVoucher = async (e) => {
    e.preventDefault();
    const id = voucherReprintId.trim();
    if (!id || downloadingVoucher) return;
    const tab = window.open("", "_blank");
    setDownloadingVoucher(true);
    try {
      const blob = await getIdCardReprintShroffVoucherPdf(id);
      openBlobInTab(blob, tab);
    } catch (err) {
      tab?.close?.();
      toast.error(errInfo(err, "Could not load the shroff voucher for that reprint id.").message);
    } finally {
      setDownloadingVoucher(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (confirming || !receiptFile) return;
    setConfirming(true);
    setError("");
    setFieldErrors({});
    try {
      const r = await confirmIdCardReprintManualPayment({
        payment_reference: paymentReference.trim(),
        paid_amount: paidAmount,
        paid_date: paidDate,
        receipt_reference: receiptReference.trim(),
        receipt: receiptFile,
        note: note.trim() || undefined,
      });
      toast.success(r.message || "Payment confirmed. Receipt pending director acknowledgement.");
      setResult(r);
      setPaymentReference("");
      setPaidAmount("");
      setReceiptReference("");
      setReceiptFile(null);
      setNote("");
    } catch (err) {
      const info = errInfo(err, "Could not submit this receipt.");
      setError(info.message);
      setFieldErrors(info.fields);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="max-w-xl space-y-4">
      {canViewVoucher && (
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-bold text-base mb-1" style={{ color: TOKENS.NAVY }}>
            Download a shroff voucher
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Enter the reprint request's id (shown on the Reprint Approvals tab after approving) to print its
            pay-at-counter voucher.
          </p>
          <form onSubmit={handleDownloadVoucher} className="flex flex-wrap gap-3">
            <input
              className={`${inputClass} flex-1 min-w-[160px] font-mono`}
              placeholder="Reprint request id, e.g. 12"
              value={voucherReprintId}
              onChange={(e) => setVoucherReprintId(e.target.value)}
            />
            <button type="submit" disabled={downloadingVoucher || !voucherReprintId.trim()} className={`${btnSecondary} !w-auto px-6`}>
              {downloadingVoucher ? "Opening…" : "Download"}
            </button>
          </form>
        </div>
      )}

      {canConfirm && (
        <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
          <div>
            <h3 className="font-bold text-base mb-1" style={{ color: TOKENS.NAVY }}>
              Submit Shroff Receipt
            </h3>
            <p className="text-sm text-gray-500">
              Records a reprint fee paid at the counter. Confirming moves the card to the print queue immediately —
              a Director/Admin will acknowledge the receipt afterward for audit purposes.
            </p>
          </div>

          <form onSubmit={handleConfirm} className="space-y-4">
            <div>
              <label className={labelClass}>Payment reference</label>
              <input
                className={`${inputClass} font-mono`}
                placeholder="From the approval response, e.g. 073…"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                required
              />
              {fieldError("payment_reference") && <p className="text-xs text-red-600 mt-1">{fieldError("payment_reference")}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>
                  Paid amount <span className="text-gray-400 font-normal">(must match payable amount)</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={inputClass}
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                  required
                />
                {fieldError("paid_amount") && <p className="text-xs text-red-600 mt-1">{fieldError("paid_amount")}</p>}
              </div>
              <div>
                <label className={labelClass}>Paid date</label>
                <input type="date" className={inputClass} value={paidDate} onChange={(e) => setPaidDate(e.target.value)} required />
                {fieldError("paid_date") && <p className="text-xs text-red-600 mt-1">{fieldError("paid_date")}</p>}
              </div>
            </div>

            <div>
              <label className={labelClass}>Receipt / slip reference</label>
              <input
                className={inputClass}
                value={receiptReference}
                onChange={(e) => setReceiptReference(e.target.value)}
                required
              />
              {fieldError("receipt_reference") && <p className="text-xs text-red-600 mt-1">{fieldError("receipt_reference")}</p>}
            </div>

            <ReceiptFileInput file={receiptFile} onChange={setReceiptFile} error={fieldError("receipt")} />

            <div>
              <label className={labelClass}>
                Note <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea className={`${inputClass} resize-none`} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button type="submit" disabled={confirming || !receiptFile} className={`${btnPrimary} w-full`}>
              {confirming ? "Confirming…" : "Confirm Payment"}
            </button>
          </form>

          {result && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-800">
              {result.message || "Payment confirmed. Receipt pending director acknowledgement."}{" "}
              {result.approval_id && (
                <>
                  Approval ID: <span className="font-mono font-semibold">#{result.approval_id}</span> — will be
                  acknowledged on the Payments page's Manual Approvals tab.
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────
export default function IdCardReprints() {
  const { can } = useAuth();
  const canSubmit = can("submit_id_card_reprint_requests");
  const canViewApprovals = can("view_id_card_reprint_approvals");
  const canApprove = can("approve_id_card_reprint_requests");
  const canReject = can("reject_id_card_reprint_requests");
  // "confirm_shroff_payments" (e.g. the Shroff role) is accepted by the
  // backend's */manual-confirm routes too, so it also unlocks this tab.
  const canConfirmPayment = can(["submit_manual_payment_receipts", "confirm_shroff_payments"]);
  const canViewVoucher = can("view_payments");

  const tabs = [
    canSubmit && { key: "submit", label: "Submit Request" },
    canViewApprovals && { key: "approvals", label: "Reprint Approvals" },
    (canConfirmPayment || canViewVoucher) && { key: "confirm", label: "Confirm Payment" },
  ].filter(Boolean);

  const [activeTab, setActiveTab] = useState(tabs[0]?.key || "submit");

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-bold" style={{ color: TOKENS.NAVY }}>
          ID Card Reprints
        </h1>
        <p className="text-sm text-gray-500">Lost-card reprint requests: submission, director approval and counter payment.</p>
      </div>

      {tabs.length === 0 ? (
        <EmptyState icon={ICONS.shield} text="You don't have permission to access any part of this module." />
      ) : (
        <>
          {tabs.length > 1 && (
            <div className="flex gap-1 mb-5 border-b border-gray-200">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition ${
                    activeTab === t.key ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  style={activeTab === t.key ? { color: TOKENS.PRIMARY } : undefined}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {activeTab === "submit" && canSubmit && <SubmitRequestTab />}
          {activeTab === "approvals" && canViewApprovals && (
            <ApprovalsTab canApprove={canApprove} canReject={canReject} canViewVoucher={canViewVoucher} />
          )}
          {activeTab === "confirm" && (canConfirmPayment || canViewVoucher) && (
            <ConfirmPaymentTab canConfirm={canConfirmPayment} canViewVoucher={canViewVoucher} />
          )}
        </>
      )}
    </div>
  );
}
